import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {clamp, filter, forEach, fromPairs, includes, keys, size, sortBy} from 'lodash';
import {useDispatch} from 'react-redux';

import {flashMessage} from 'shared/actions/notifications';
import {skipChecklistItem} from './actions';
import {ChecklistItem as ChecklistItemType} from 'src/types/store';

import Icon from 'shared/components/icon';
import StepProgress from 'shared/components/step-progress';
import ChecklistFooter from './checklist-footer';
import {ChecklistItem, FinishedItem} from './checklist-item';
import CitizenshipModal from 'src/components/citizenship-modal';

interface Props {
  skippedChecklistItems: string[];
  firstName: string;
  onDismiss: () => void;
  tipContent: string;
  listItems: Record<string, ChecklistItemType>;
  onCourseAdd: () => void;
}

const Checklist = ({
  skippedChecklistItems = [],
  firstName,
  onDismiss,
  tipContent,
  listItems,
  onCourseAdd,
}: Props) => {
  const dispatch = useDispatch();
  const checklistItems = useMemo(
    () => keys(listItems).filter((id) => !includes(skippedChecklistItems, id)),
    [listItems, skippedChecklistItems]
  );
  const completedChecklistItems = useMemo(
    () => fromPairs(checklistItems.map((id) => [id, listItems[id].isComplete()])),
    [checklistItems, listItems]
  );
  const [activeItemIndex, setActiveItemIndex] = useState(filter(completedChecklistItems).length);
  const [itemsCompleted, setItemsCompleted] = useState(completedChecklistItems);
  const [skipChecklist, setSkipChecklist] = useState(false);

  // Check if checklist is already completed
  useEffect(() => {
    if (filter(itemsCompleted).length === checklistItems.length) {
      setSkipChecklist(true);
    }
  }, [checklistItems, itemsCompleted]);

  // Handle items getting completed
  useEffect(() => {
    // Currently completed checklist items.
    const numItemsPreviouslyCompleted = filter(itemsCompleted).length;
    // Compute next completed checklist items.
    const newItemsCompleted = completedChecklistItems;
    const numItemsCompleted = filter(newItemsCompleted).length;

    if (numItemsPreviouslyCompleted !== numItemsCompleted) {
      if (numItemsCompleted > numItemsPreviouslyCompleted) {
        // Show a message.
        dispatch(flashMessage('Checklist item saved!'));
        setActiveItemIndex((current) => Math.min(checklistItems.length - 1, current + 1));
      }
      setItemsCompleted(newItemsCompleted);
    }
  }, [checklistItems, completedChecklistItems, dispatch, itemsCompleted]);

  useEffect(() => {
    setActiveItemIndex((current) =>
      clamp(current, 0, size(listItems) - skippedChecklistItems.length - 1)
    );
  }, [listItems, skippedChecklistItems]);

  const sortByOrder = useCallback(
    (items: string[]) => sortBy(items, (id) => listItems[id].order),
    [listItems]
  );

  const itemOrdering = useMemo(() => {
    // Completed checklist items go to the front.
    // Items will be in the same order with respect to other complete/incomplete items.
    const completeItems: string[] = [];
    const incompleteItems: string[] = [];

    forEach(completedChecklistItems, (complete, id) => {
      if (complete) {
        completeItems.push(id);
      } else {
        incompleteItems.push(id);
      }
    });

    return sortByOrder(completeItems).concat(sortByOrder(incompleteItems));
  }, [completedChecklistItems, sortByOrder]);

  const handleSkipChecklistItem = () => {
    dispatch(skipChecklistItem(itemOrdering[activeItemIndex]));
  };
  const nextItem = () =>
    setActiveItemIndex((current) => Math.min(checklistItems.length - 1, current + 1));
  const previousItem = () => setActiveItemIndex((current) => Math.max(0, current - 1));

  const itemId = itemOrdering[activeItemIndex];
  const item = itemId ? listItems[itemId] : undefined;
  const itemsComplete = filter(itemsCompleted).length;
  const itemsTotal = checklistItems.length;
  const checklistComplete = itemsComplete === itemsTotal;
  const completed = itemId ? itemsCompleted[itemId] : true;

  if (skipChecklist) return null;

  return (
    <div className="portfolio-checklist-card">
      <CitizenshipModal />
      <div className="checklist-header">
        <h2 className="portfolio-checklist-card-title">Getting Started</h2>
        {checklistComplete && (
          <button
            onClick={onDismiss}
            aria-label="dismiss checklist"
            className="dismiss-checklist-btn"
          >
            <Icon iconType="times-r" />
          </button>
        )}
      </div>
      <StepProgress
        iconIndex={activeItemIndex}
        itemsComplete={itemsComplete}
        itemsTotal={itemsTotal}
      />
      <div className="checklist-items">
        {checklistComplete || !item ? (
          <FinishedItem firstName={firstName} />
        ) : (
          <ChecklistItem
            title={item.title}
            description={item.description}
            cta={item.cta}
            url={item.url}
            completed={completed}
          />
        )}
      </div>
      <ChecklistFooter
        onPreviousItem={previousItem}
        onNextItem={nextItem}
        onSkipChecklistItem={handleSkipChecklistItem}
        onCourseAdd={onCourseAdd}
        activeItemIndex={activeItemIndex}
        itemComplete={completed}
        checklistComplete={checklistComplete}
        itemsTotal={itemsTotal}
        item={item}
        tipContent={tipContent}
      />
    </div>
  );
};

export default Checklist;
